import React, { useState } from "react";
import { FormField } from "@jugl-web/rest-api";
import { UtilityTextInputBase } from "@jugl-web/ui-components/cross-platform/forms/UtilityTextInputBase";
import {
  PhoneInput,
  validatePhoneNumber,
} from "@jugl-web/ui-components/cross-platform/PhoneInput";
import { customerPhoneStringToInputValue } from "@jugl-web/ui-components/cross-platform/PhoneInput/utils/customerPhoneStringToInputValue";
import { inputValueToCustomerPhoneString } from "@jugl-web/ui-components/cross-platform/PhoneInput/utils/inputValueToCustomerPhoneString";
import { TextInput } from "@jugl-web/ui-components/cross-platform";
import {
  Controller,
  UseFormRegister,
  FieldValues,
  Control,
  FieldError,
} from "react-hook-form";
import {
  convertSnakeCaseToReadableString,
  isValidEmail,
  useTranslations,
  isValidUrl,
} from "@jugl-web/utils";
import { SelectTrigger } from "@jugl-web/ui-components/cross-platform/forms/SelectTrigger";
import { customerFieldDescriptorsById } from "./constants";
import { CustomerDropdownPicker } from "../CustomerDropdownPicker/CustomerDropdownPicker";

export const CustomerFormField: React.FC<{
  section: "personal" | "company";
  field: FormField;
  control: Control<FieldValues>;
  register: UseFormRegister<FieldValues>;
  value: string;
  invalid?: boolean;
  error?: FieldError;
  variant?: "task-dialog" | "customer-table";
  inEditMode?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
}> = ({ section, field, register, control, value, invalid, error }) => {
  const [focusState, setFocusState] = useState<{ [key: string]: boolean }>({});

  const { t } = useTranslations();

  const convertPhoneStringToObject = (str: string) => {
    if (!str) {
      return { iso: "IN", code: "+91", phone: "" };
    }
    return customerPhoneStringToInputValue(str);
  };

  if (field.section === section) {
    const normalizedFieldName = convertSnakeCaseToReadableString(field.name);
    switch (field.type) {
      case "email":
      case "text":
      case "url":
        return (
          <div>
            <TextInput
              type={field.type === "url" ? "url" : "text"}
              placeholder={t({
                id: "common.enter",
                defaultMessage: "Enter",
              })}
              isInvalid={invalid || !!error}
              isRequired={field.required}
              label={
                customerFieldDescriptorsById[field.name]
                  ? t(customerFieldDescriptorsById[field.name])
                  : normalizedFieldName
              }
              classNames={{
                labelClassName: "capitalize",
              }}
              {...register(field.id, {
                required: field.required,
                pattern: {
                  value: /.*[^ ].*/,
                  message: t({
                    id: "feedback.only-spaces-are-forbidden",
                    defaultMessage: "Field can't contain only spaces",
                  }),
                },
                validate: (inputValue) => {
                  if (
                    (field.type !== "email" && field.type !== "url") ||
                    !inputValue
                  ) {
                    return true;
                  }
                  if (field.type === "url" && !isValidUrl(inputValue)) {
                    return t({
                      id: "feedback.enter-valid-url-address",
                      defaultMessage: "Enter a valid URL address",
                    });
                  }
                  if (field.type === "email" && !isValidEmail(inputValue)) {
                    return t({
                      id: "feedback.invalid-email",
                      defaultMessage: "Invalid email",
                    });
                  }
                  return true;
                },
              })}
              errorMessage={error?.message}
            />
          </div>
        );
      case "dropdown":
        return (
          <Controller
            control={control}
            name={field.id}
            rules={{ required: field.required }}
            render={({ field: controlledField }) => (
              <CustomerDropdownPicker
                field={field}
                controlledField={controlledField}
                renderTrigger={({ Trigger, triggerRef }) => {
                  const selectedValue = field.values?.find(
                    (fieldValue) => fieldValue.id === controlledField.value
                  );
                  return (
                    <UtilityTextInputBase
                      isRequired={field.required}
                      isInvalid={invalid || !!error}
                      label={normalizedFieldName}
                      classNames={{
                        wrapperClassName: "capitalize",
                      }}
                    >
                      <Trigger
                        ref={triggerRef}
                        as={SelectTrigger}
                        isInvalid={invalid || !!error}
                        className="relative"
                        label={
                          selectedValue?.value ||
                          t({
                            id: "common.select",
                            defaultMessage: "Select",
                          })
                        }
                        hasValue={controlledField.value}
                      />
                    </UtilityTextInputBase>
                  );
                }}
              />
            )}
          />
        );
      case "mobile":
        return (
          <Controller
            control={control}
            {...register(field.id, {
              required: field.required,
              validate: {
                check: (inputValue) => {
                  if (convertPhoneStringToObject(inputValue || "").phone) {
                    return validatePhoneNumber(inputValue);
                  }
                  return true;
                },
              },
            })}
            render={({ field: controlledField }) => (
              <UtilityTextInputBase
                label={
                  customerFieldDescriptorsById[field.name]
                    ? t(customerFieldDescriptorsById[field.name])
                    : normalizedFieldName
                }
                isRequired={field.required}
                isInvalid={invalid || !!error}
              >
                <PhoneInput
                  value={convertPhoneStringToObject(
                    controlledField.value || ""
                  )}
                  placeholder={t({
                    id: "common.enter",
                    defaultMessage: "Enter",
                  })}
                  onChange={({
                    iso,
                    phone,
                    code,
                  }: {
                    iso: string;
                    phone: string;
                    code: string;
                  }) => {
                    controlledField.onChange(
                      inputValueToCustomerPhoneString({ iso, code, phone })
                    );
                  }}
                  onFocus={() => {
                    setFocusState({ ...focusState, [field.id]: true });
                  }}
                  onBlur={() => {
                    setFocusState({ ...focusState, [field.id]: false });
                  }}
                />
              </UtilityTextInputBase>
            )}
          />
        );
      default:
        return <></>;
    }
  }
  return <></>;
};
